import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useAuth } from '../../providers/AuthProvider';
import {
  isValidEmail,
  validatePasswordPolicy,
} from '../../services/auth-validation';
import { FederatedButtons } from './FederatedButtons';
import { OrSeparator } from './OrSeparator';
import { AppLogo } from '../AppLogo';
import { getPrivacyUrl } from '../../services/runtime-config';


interface SignUpFormProps {
  /** Switch back to the sign-in view. */
  onSwitchToSignIn: () => void;
  /**
   * Called on a successful registration, carrying the entered email and the
   * masked destination so the verify view can display it (Req 2.2, 3.3).
   */
  onRegistered: (email: string, destinationMasked?: string) => void;
}

/**
 * A single per-rule row in the inline password-policy checklist (Req 1.4).
 */
function PolicyRule({ met, label }: { met: boolean; label: string }) {
  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.4rem',
        color: met ? 'var(--color-success)' : 'var(--color-muted)',
      }}
    >
      <span aria-hidden="true">{met ? '\u2713' : '\u25CB'}</span>
      <span>{label}</span>
    </li>
  );
}

/**
 * Registration form (email + password) with inline, per-rule password-policy
 * feedback and inline email validity (Req 1.1, 1.4, 1.8). On submit it calls
 * `register`, then hands the entered email + masked destination to the parent
 * so the verify view can be shown (Req 1.2, 2.2).
 *
 * Validates: Requirements 1.1, 1.2, 1.3, 1.4, 1.8, 5.1, 5.2, 10.5, 10.6, 10.7.
 */
export function SignUpForm({ onSwitchToSignIn, onRegistered }: SignUpFormProps) {
  // Privacy information URL, resolved from runtime config (per-env
  // marketing/privacy URL) with a built-in fallback (Path 2 domain migration).
  const PRIVACY_URL = getPrivacyUrl();
  const { register, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const headingRef = useRef<HTMLHeadingElement>(null);

  // Place initial focus on the view heading when this view mounts (Req 10.6).
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const policy = validatePasswordPolicy(password);
  const emailValid = isValidEmail(email);
  const emailInvalid = emailTouched && email.length > 0 && !emailValid;
  const canSubmit = emailValid && policy.isValid && !submitting;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);
    setLocalError(null);
    try {
      const result = await register(email, password);
      onRegistered(email, result.destinationMasked);
    } catch (err) {
      // AuthProvider already sets a non-enumerating `error`; keep a local
      // fallback message so the alert always has content.
      setLocalError(err instanceof Error ? err.message : 'Registration failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const shownError = localError ?? error;

  return (
    <form
      onSubmit={handleSubmit}
      style={{ width: '100%', maxWidth: '400px' }}
      aria-label="Create account form"
    >
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
        <AppLogo size={56} />
      </div>
      <h1 ref={headingRef} className="focus-target" tabIndex={-1} style={{ marginBottom: '1.5rem' }}>
        Create an account
      </h1>

      {shownError && (
        <div
          role="alert"
          style={{ color: 'var(--color-danger)', marginBottom: '1rem' }}
        >
          {shownError}
        </div>
      )}

      <div style={{ marginBottom: '1rem' }}>
        <label
          htmlFor="signup-email"
          style={{ display: 'block', marginBottom: '0.25rem' }}
        >
          Email
        </label>
        <input
          id="signup-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => setEmailTouched(true)}
          required
          autoComplete="email"
          aria-invalid={emailInvalid}
          aria-describedby={emailInvalid ? 'signup-email-error' : undefined}
          style={{ width: '100%' }}
        />
        {emailInvalid && (
          <p
            id="signup-email-error"
            style={{
              color: 'var(--color-danger)',
              margin: '0.25rem 0 0',
              fontSize: '0.85rem',
            }}
          >
            Enter a valid email address.
          </p>
        )}
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <label
          htmlFor="signup-password"
          style={{ display: 'block', marginBottom: '0.25rem' }}
        >
          Password
        </label>
        <input
          id="signup-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="new-password"
          aria-describedby="password-policy"
          style={{ width: '100%' }}
        />
        <ul
          id="password-policy"
          aria-label="Password requirements"
          style={{
            listStyle: 'none',
            padding: 0,
            margin: '0.5rem 0 0',
            fontSize: '0.85rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.2rem',
          }}
        >
          <PolicyRule met={policy.minLength} label="At least 8 characters" />
          <PolicyRule met={policy.hasUppercase} label="An uppercase letter" />
          <PolicyRule met={policy.hasLowercase} label="A lowercase letter" />
          <PolicyRule met={policy.hasDigit} label="A digit" />
          <PolicyRule met={policy.hasSymbol} label="A symbol" />
        </ul>
      </div>

      <button
        type="submit"
        disabled={!canSubmit}
        style={{
          width: '100%',
          padding: '0.75rem',
          backgroundColor: 'var(--color-brand)',
          color: 'var(--color-on-brand)',
          border: 'none',
          borderRadius: '4px',
          cursor: canSubmit ? 'pointer' : 'not-allowed',
          fontSize: '1rem',
          opacity: canSubmit ? 1 : 0.7,
        }}
      >
        {submitting ? 'Creating account...' : 'Create account'}
      </button>

      <p style={{ marginTop: '1rem', textAlign: 'center' }}>
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
          Privacy information
        </a>
      </p>

      <OrSeparator />

      <FederatedButtons />

      <p style={{ marginTop: '1.5rem', textAlign: 'center' }}>
        Already have an account?{' '}
        <button
          type="button"
          onClick={onSwitchToSignIn}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            color: 'var(--color-link)',
            cursor: 'pointer',
            textDecoration: 'underline',
            fontSize: 'inherit',
          }}
        >
          Sign in
        </button>
      </p>
    </form>
  );
}
